// Mascote "Nexo" (maçã) — fiel ao protótipo avatar-naturexpress.html, adaptado para um componente do sistema.
//
// Isolamento: todas as regras de estilo vivem escopadas sob `.nx-nexo-svg` (prefixo próprio), sem importar
// estilos globais nem fontes externas. Sem áudio em nenhuma pose ("falando" é só a boca animada — sem som e
// sem acesso ao microfone). Respeita movimento reduzido (prop + media query) e pausa quando oculto (prop).
//
// Cinco poses (mesmo SVG, sem mascote paralelo) — exatamente como o original:
//  - idle    (respirando): corpo respira;
//  - wave    (acenando):   braço acena em loop + respiração;
//  - jumping (pulando):    salto com deformação do corpo + braços para cima;
//  - talking (falando):    boca abre/fecha (troca sorriso↔boca aberta) + respiração mais rápida;
//  - happy   (feliz):      corpo balança (spin), olhos viram "felizes" e corações sobem.
// Mais: piscadas periódicas e PUPILAS que acompanham o cursor (como no original).
import { useEffect, useRef } from "react";

export type NexoAnimation = "idle" | "wave" | "happy" | "talking" | "jumping";

const MODE: Record<NexoAnimation, string> = {
  idle: "idle",
  wave: "wave",
  happy: "happy",
  talking: "talk",
  jumping: "jump",
};

type NexoMascotProps = {
  animation?: NexoAnimation;
  /** Desliga qualquer animação/transição (preferência do usuário); a media query do SO também desliga. */
  reduceMotion?: boolean;
  /** Congela a animação sem trocar a pose (ex.: aba oculta / aba interna inativa). */
  paused?: boolean;
  size?: number;
  className?: string;
  title?: string;
};

export function NexoMascot({
  animation = "idle",
  reduceMotion = false,
  paused = false,
  size = 56,
  className,
  title = "Nexo, mascote da Naturexpress",
}: NexoMascotProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pupilsRef = useRef<SVGGElement>(null);

  // Pupilas acompanham o cursor (como no original). Desligado sob movimento reduzido.
  useEffect(() => {
    if (reduceMotion) {
      if (pupilsRef.current) pupilsRef.current.style.transform = "";
      return;
    }
    const onMove = (e: PointerEvent) => {
      const svg = svgRef.current;
      const pupils = pupilsRef.current;
      if (!svg || !pupils) return;
      const r = svg.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height * 0.5;
      const dx = Math.max(-1, Math.min(1, (e.clientX - cx) / 300));
      const dy = Math.max(-1, Math.min(1, (e.clientY - cy) / 300));
      pupils.style.transform = `translate(${dx * 6}px, ${dy * 5}px)`;
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [reduceMotion]);

  const cls = [
    "nx-nexo-svg",
    `nx-m-${MODE[animation]}`,
    reduceMotion ? "nx-nexo-reduce" : "",
    !reduceMotion && paused ? "nx-nexo-paused" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <svg
      ref={svgRef}
      className={cls}
      width={size}
      height={size}
      viewBox="0 0 320 330"
      role="img"
      aria-label={title}
    >
      <style>{NEXO_CSS}</style>
      <ellipse cx="160" cy="318" rx="80" ry="9" fill="#000" opacity=".08" />
      <g className="nx-body">
        {/* corações (feliz) */}
        <path className="nx-heart" style={{ animationDelay: "0s" }} d="M70 70c-6-8-18-2-12 7l12 11 12-11c6-9-6-15-12-7z" fill="#e94b4b" />
        <path className="nx-heart" style={{ animationDelay: ".5s" }} d="M250 60c-5-7-15-2-10 6l10 9 10-9c5-8-5-13-10-6z" fill="#7a2fa0" />
        <path className="nx-heart" style={{ animationDelay: "1s" }} d="M262 120c-4-6-12-1-8 5l8 7 8-7c4-6-4-11-8-5z" fill="#f8a000" />
        {/* braços */}
        <g className="nx-arm-l">
          <path d="M60 200 Q30 210 28 240" stroke="#34b233" strokeWidth="10" fill="none" strokeLinecap="round" />
          <circle cx="28" cy="244" r="11" fill="#34b233" />
        </g>
        <g className="nx-arm-r">
          <path d="M260 200 Q290 210 292 240" stroke="#34b233" strokeWidth="10" fill="none" strokeLinecap="round" />
          <circle cx="292" cy="244" r="11" fill="#34b233" />
        </g>
        {/* maçã */}
        <path
          d="M160 92 C130 70 80 72 60 110 C38 150 46 220 80 262 C105 294 130 300 160 292 C190 300 215 294 240 262 C274 220 282 150 260 110 C240 72 190 70 160 92 Z"
          fill="#ffffff"
          stroke="#34b233"
          strokeWidth="11"
          strokeLinejoin="round"
        />
        {/* folha */}
        <g className="nx-leaf">
          <path d="M160 92 C158 70 164 50 168 40" stroke="#34b233" strokeWidth="7" fill="none" strokeLinecap="round" />
          <path d="M166 74 C162 34 196 10 232 8 C232 46 206 76 166 74 Z" fill="#34b233" />
          <path d="M172 68 C186 46 202 32 222 20" stroke="#ffffff" strokeWidth="4" fill="none" strokeLinecap="round" />
        </g>
        {/* rosto — pupilas acompanham o cursor */}
        <g className="nx-pupils" ref={pupilsRef}>
          <ellipse className="nx-eye" cx="125" cy="170" rx="13" ry="17" fill="#22301f" />
          <ellipse className="nx-eye" cx="195" cy="170" rx="13" ry="17" fill="#22301f" />
          <circle className="nx-eye" cx="129" cy="164" r="4.5" fill="#fff" />
          <circle className="nx-eye" cx="199" cy="164" r="4.5" fill="#fff" />
        </g>
        {/* olhos "felizes" (modo happy) */}
        <g className="nx-happy-eyes" stroke="#22301f" strokeWidth="6" fill="none" strokeLinecap="round">
          <path d="M112 174 Q125 156 138 174" />
          <path d="M182 174 Q195 156 208 174" />
        </g>
        <ellipse cx="100" cy="200" rx="14" ry="8" fill="#ff9b9b" opacity=".75" />
        <ellipse cx="220" cy="200" rx="14" ry="8" fill="#ff9b9b" opacity=".75" />
        <path className="nx-mouth-smile" d="M138 200 Q160 224 182 200" stroke="#22301f" strokeWidth="6" fill="none" strokeLinecap="round" />
        <path className="nx-mouth-open" d="M140 198 Q160 232 180 198 Z" fill="#e94b4b" stroke="#22301f" strokeWidth="5" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

const NEXO_CSS = `
.nx-nexo-svg{overflow:visible;display:block}
.nx-nexo-svg .nx-body{transform-origin:160px 300px;transform-box:view-box}
.nx-nexo-svg .nx-leaf{transform-origin:160px 78px;transform-box:view-box;animation:nx-leaf 2.4s ease-in-out infinite}
.nx-nexo-svg .nx-eye{transform-origin:center;transform-box:fill-box;animation:nx-blink 4s infinite}
.nx-nexo-svg .nx-pupils{transition:transform .15s ease-out}
.nx-nexo-svg .nx-arm-r{transform-origin:262px 200px;transform-box:view-box}
.nx-nexo-svg .nx-arm-l{transform-origin:58px 200px;transform-box:view-box}
.nx-nexo-svg .nx-mouth-open{opacity:0}
.nx-nexo-svg .nx-happy-eyes{opacity:0}
.nx-nexo-svg .nx-heart{opacity:0;transform-box:fill-box}

.nx-m-idle .nx-body{animation:nx-breathe 2.6s ease-in-out infinite}

.nx-m-wave .nx-body{animation:nx-breathe 2.6s ease-in-out infinite}
.nx-m-wave .nx-arm-r{animation:nx-wave .6s ease-in-out infinite alternate}

.nx-m-jump .nx-body{animation:nx-jump .9s cubic-bezier(.3,0,.4,1) infinite}
.nx-m-jump .nx-arm-r{animation:nx-up .9s ease-in-out infinite}
.nx-m-jump .nx-arm-l{animation:nx-upl .9s ease-in-out infinite}

.nx-m-talk .nx-body{animation:nx-breathe 1.4s ease-in-out infinite}
.nx-m-talk .nx-mouth-smile{opacity:0}
.nx-m-talk .nx-mouth-open{opacity:1;transform-origin:center;transform-box:fill-box;animation:nx-talk .28s ease-in-out infinite alternate}

.nx-m-happy .nx-body{animation:nx-spin 1.6s ease-in-out infinite}
.nx-m-happy .nx-eye{animation:none;opacity:0}
.nx-m-happy .nx-happy-eyes{opacity:1}
.nx-m-happy .nx-heart{animation:nx-float 1.6s ease-out infinite}

@keyframes nx-leaf{50%{transform:rotate(-12deg)}}
@keyframes nx-blink{0%,92%,100%{transform:scaleY(1)}95%{transform:scaleY(.1)}}
@keyframes nx-breathe{50%{transform:translateY(-6px) scale(1.02,.98)}}
@keyframes nx-wave{from{transform:rotate(-10deg)}to{transform:rotate(-55deg)}}
@keyframes nx-jump{0%,100%{transform:translateY(0) scale(1.06,.92)}15%{transform:translateY(0) scale(1)}50%{transform:translateY(-55px) scale(.97,1.04)}85%{transform:translateY(0) scale(1)}}
@keyframes nx-up{50%{transform:rotate(-70deg)}}
@keyframes nx-upl{50%{transform:rotate(70deg)}}
@keyframes nx-talk{from{transform:scaleY(.35)}to{transform:scaleY(1)}}
@keyframes nx-spin{0%{transform:rotate(0)}25%{transform:rotate(-8deg)}75%{transform:rotate(8deg)}100%{transform:rotate(0)}}
@keyframes nx-float{0%{opacity:0;transform:translateY(0) scale(.6)}30%{opacity:1}100%{opacity:0;transform:translateY(-60px) scale(1)}}

.nx-nexo-paused .nx-body,.nx-nexo-paused .nx-leaf,.nx-nexo-paused .nx-eye,.nx-nexo-paused .nx-arm-r,.nx-nexo-paused .nx-arm-l,.nx-nexo-paused .nx-mouth-open,.nx-nexo-paused .nx-heart{animation-play-state:paused!important}
.nx-nexo-reduce *{animation:none!important;transition:none!important}
@media (prefers-reduced-motion:reduce){.nx-nexo-svg *{animation:none!important;transition:none!important}}
`;
